// src/pages/admin/LeadDetailPage.js
import React, { useState, useEffect } from 'react';
import { useParams, Link} from "react-router-dom";
import { getAdminLeadDetails, getLeadConversation } from "../../../../services/AdminApis";
import { format } from "date-fns";
import {
  AlertCircle, Mail, Phone, MapPin, Coins, Building, Hash, User, Tag,
  Globe, Smartphone, Megaphone, Waypoints, ExternalLink, CheckCircle2, XCircle, Globe2, Computer, Copy
} from "lucide-react";


// Import shadcn/ui components
import { Alert, AlertDescription, AlertTitle } from "../../../../components/ui/alert";
import { Card, CardContent, CardHeader, CardTitle } from "../../../../components/ui/card";
import { Badge } from "../../../../components/ui/badge";
import { Button } from "../../../../components/ui/button";
import { Skeleton } from "../../../../components/ui/skeleton";
import { Separator } from "../../../../components/ui/separator";

// Import custom sub-components
import { LeadInteractionsTable } from "./LeadInteractionsTable";
import { LeadDetailCard } from "./LeadDetailCard";
import { usePermissions } from '../../../../hooks/usePermissions';
import { useToast } from '../../../../hooks/use-toast';
import { UpdateLeadStatusDialog } from './UpdateLeadStatusDialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../../../components/ui/tooltip";
import { countryCodeToFlag } from "../../../../utility/countryFlagEmogii";
import AdminNotesSection from '../../../../components/Admin/SellerManagement/Notes/AdminNotesSection';
import LeadCommsSection from './LeadCommsSection';
import ConversationDialog from './ConversationDialog';

const getS3AssetUrl = (fileKey) => {
  if (!fileKey) return null;

  const key = String(fileKey);
  if (/^(https?:)?\/\//i.test(key)) {
    return key;
  }

  const baseUrl = process.env.REACT_APP_S3_ASSETS_FETCH_URL || "";
  if (!baseUrl) {
    return key;
  }

  return `${baseUrl.replace(/\/$/, "")}/${key.replace(/^\//, "")}`;
};

const getFileKey = (file) => {
  if (!file) return "";
  if (typeof file === "string") return file;

  return file.key || file.url || file.file_key || file.path || "";
};

const getFileName = (file, index) => {
  if (file && typeof file === "object" && file.name) {
    return file.name;
  }

  const fileKey = getFileKey(file);
  return fileKey.split("/").filter(Boolean).pop() || `File ${index + 1}`;
};

const formatDetailLabel = (key) =>
  key
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const hasDisplayValue = (value) => {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "object") return Object.keys(value).length > 0;
  return true;
};

const AdditionalDetailValue = ({ value }) => {
  if (Array.isArray(value)) {
    return (
      <div className="space-y-2">
        {value.map((item, index) => (
          <div key={index}>
            <AdditionalDetailValue value={item} />
          </div>
        ))}
      </div>
    );
  }

  if (value && typeof value === "object") {
    const entries = Object.entries(value).filter(([, nestedValue]) =>
      hasDisplayValue(nestedValue)
    );

    if (entries.length === 0) {
      return <span className="text-muted-foreground">N/A</span>;
    }

    return (
      <div className="space-y-2 rounded-md border bg-gray-50 p-3">
        {entries.map(([nestedKey, nestedValue]) => (
          <div key={nestedKey} className="grid gap-1 sm:grid-cols-3">
            <span className="font-medium text-gray-600">
              {formatDetailLabel(nestedKey)}
            </span>
            <div className="sm:col-span-2 text-gray-800">
              <AdditionalDetailValue value={nestedValue} />
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <span className="whitespace-pre-wrap break-words">
      {String(value)}
    </span>
  );
};

const VerifiedIcon = ({ isVerified, verifiedText, notVerifiedText }) => (
  <TooltipProvider delayDuration={100}>
    <Tooltip>
      <TooltipTrigger>
        {isVerified ? (
          <CheckCircle2 className="h-5 w-5 text-green-600" />
        ) : (
          <XCircle className="h-5 w-5 text-gray-400" />
        )}
      </TooltipTrigger>
      <TooltipContent>
        <p>{isVerified ? verifiedText : notVerifiedText}</p>
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

const LeadDetailPage = () => {
  const { leadId } = useParams();

  // 2. Set up state variables for data, loading, and error
  const [lead, setLead] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const { hasPermission } = usePermissions();
  const { toast } = useToast();

  // 3. Use useEffect to fetch data when the component mounts or leadId changes
  useEffect(() => {
    // Don't fetch if there's no leadId
    if (!leadId) {
      setIsLoading(false);
      return;
    }

    const fetchLeadData = async () => {
      setIsLoading(true); // Set loading before the request
      setError(null); // Clear any previous errors
      try {
        const data = await getAdminLeadDetails(leadId);
        setLead(data);
      } catch (err) {
        setError(err); // Set error state if the fetch fails
      } finally {
        setIsLoading(false); // Set loading to false after the request is complete
      }
    };

    fetchLeadData();
  }, [leadId]); // The dependency array ensures this effect runs again if leadId changes

  useEffect(() => {
              if (lead) {
                  const displayName = lead.buyer_name;
                  document.title = `L | ${displayName} - Lead Management`;
              }
              
              // Reset title when the component unmounts (leaving the page)
              return () => {
                  document.title = "Lead Management";
              };
          }, [lead]);

  const handleStatusUpdate = (updatedLead) => {
    setLead(updatedLead); // Refresh the page data with the response from the API
  };

  const copyTextToClipboard = async (textToCopy, successDescription) => {
    try {
      if (!navigator?.clipboard?.writeText) {
        throw new Error("Clipboard API not available");
      }

      await navigator.clipboard.writeText(textToCopy);
      toast({
        title: "Copied",
        description: successDescription,
      });
    } catch (copyError) {
      console.error("Failed to copy text:", copyError);
      toast({
        variant: "destructive",
        title: "Copy failed",
        description: "Could not copy to clipboard. Please try again.",
      });
    }
  };

  if (isLoading) {
    return <LeadDetailSkeleton />;
  }

  if (error) {
    return (
      <div className="p-4">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error Fetching Lead Details</AlertTitle>
          <AlertDescription>
            {error.response?.data?.detail || error.message}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // If loading is finished but we have no lead data
  if (!lead) {
      return (
        <div className="p-4">
            <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>No Lead Data</AlertTitle>
                <AlertDescription>
                    Could not find details for the specified lead.
                </AlertDescription>
            </Alert>
        </div>
      );
  }

  // --- The rest of the component's JSX is identical to before ---

  const leadCreatedAt = lead.created_at ? format(new Date(lead.created_at), "PPP p") : "N/A";
  const emailVerificationStatus = lead.verification?.cognito_email_is_verified_api ? "Verified" : "Not Verified";
  const phoneVerificationStatus = lead.verification?.cognito_phone_is_verified_api ? "Verified" : "Not Verified";
  const locationText = lead.full_address || [lead.city, lead.state, lead.post_code, lead.country].filter(Boolean).join(", ") || "N/A";
  const additionalDetails = lead.project_additional_details || lead.additional_details || {};
  const additionalDetailEntries = Object.entries(additionalDetails).filter(
    ([key, value]) => key !== "files" && hasDisplayValue(value)
  );
  const additionalFiles = Array.isArray(additionalDetails.files)
    ? additionalDetails.files
        .map((file, index) => ({
          key: getFileKey(file),
          name: getFileName(file, index),
        }))
        .filter((file) => file.key)
    : [];
  const hasAdditionalDetails =
    additionalDetailEntries.length > 0 || additionalFiles.length > 0;
  const leadDetailsToCopy = [
    `Buyer Name: ${lead.buyer_name || "N/A"}`,
    `Buyer ID: ${lead.buyer_id || "N/A"}`,
    `Buyer Email: ${lead.buyer_email || "N/A"}`,
    `Email Verification Status: ${emailVerificationStatus}`,
    `Buyer Phone: ${lead.buyer_phone || "N/A"}`,
    `Phone Verification Status: ${phoneVerificationStatus}`,
    `Location: ${locationText}`,
    `Lead Created Date: ${leadCreatedAt}`,
    `Lead Link: https://sanctum.selyst.com/admin/lead/${leadId}`
  ].join("\n");

  // Prepare data for detail cards
  const buyerDetails = [
        {
          icon: Mail,
          label: "Email",
          value: (
            <div className="flex items-center gap-2">
              <span>{lead.buyer_email || "N/A"}</span>
              <VerifiedIcon 
                isVerified={lead.verification?.cognito_email_is_verified_api}
                verifiedText="Email is verified"
                notVerifiedText="Email is not verified"
              />
            </div>
          ),
        },
        {
          icon: Phone,
          label: "Phone",
          value: (
            <div className="flex items-center gap-2">
              <span>{lead.buyer_phone || "N/A"}</span>
              <VerifiedIcon 
                isVerified={lead.verification?.cognito_phone_is_verified_api}
                verifiedText="Phone is verified"
                notVerifiedText="Phone is not verified"
              />
            </div>
          )
        },
    ];

  const locationDetails = [
      { icon: MapPin, label: "Address", value: lead.full_address || 'N/A' },
      { icon: Building, label: "City", value: lead.city || 'N/A' },
      { icon: MapPin, label: "State", value: lead.state || 'N/A' },
      { icon: Hash, label: "Post Code", value: lead.post_code || 'N/A' },
      { icon: Globe, label: "Country", value: `${lead.country} ${countryCodeToFlag(lead?.country_code || 'IN' )}` || 'N/A'}
  ];

  const pricingDetails = [
      { icon: Coins, label: "Calculated Price", value: `${lead.calculated_price_coins || 0} Coins` },
  ];

  const attributionDetails = lead.attribution ? [
    { icon: Megaphone, label: "Campaign", value: lead.attribution.utm_campaign || 'N/A' },
    { icon: Waypoints, label: "Source / Medium", value: `${lead.attribution.utm_source || 'N/A'} / ${lead.attribution.utm_medium || 'N/A'}` },
    { icon: Globe, label: "IP Address", value: `${lead.attribution.ip_address || 'N/A'} (${lead.attribution.country || 'Unknown'})` },
    { icon: ExternalLink, label: "Referring Domain", value: lead.attribution.full_referrer_url || 'N/A' },
    { icon: Smartphone, label: "Device Type", value: lead.attribution.device_type || 'N/A' },
    { icon: Computer, label: "OS", value: lead.attribution.operating_system || 'N/A'},
    { icon: Globe2, label: "Browser", value: lead.attribution.browser_name || 'N/A'},
  ] : [];
  
  return (
    <div className="bg-gray-50 min-h-screen p-2 sm:p-4">
      <div className="max-w-7xl mx-auto space-y-4">
        {/* --- Header --- */}
        <Card>
          <CardHeader>
            <div className="flex justify-between items-start">
              <div>
                <CardTitle className="text-2xl text-gray-800 flex items-center gap-2">
                  <User className="w-6 h-6 text-primary" />
                  {lead.buyer_name} <p className='text-sm text-muted-foreground'>
                    <Link to={`/admin/buyers/${lead.buyer_id}/view`} 
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-blue-600 hover:text-blue-800">
                    (Buyer ID: {lead.buyer_id}) 
                    </Link></p>
                </CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  Lead ID: {lead.id} | Created:{" "}
                  {leadCreatedAt}
                </p>
                <Badge variant="secondary" className="flex items-center gap-1 w-fit text-sm">
                  <Tag className="w-3 h-3" />
                  {lead.category_name}
                </Badge>
              </div>
              <div className="flex flex-col items-end gap-2">
                <div className='flex flex-wrap justify-end gap-2'>
                  <Badge className="text-md">{lead.status}</Badge>
                  {hasPermission("cx:update-lead-status") && (
                      <UpdateLeadStatusDialog 
                          leadId={lead.id}
                          currentStatus={lead.status}
                          onUpdateSuccess={handleStatusUpdate}
                      />
                    )}
                </div>
                <div className="flex flex-wrap justify-end gap-2">
                  <ConversationDialog
                    title={lead.buyer_name}
                    fetchConversation={() => getLeadConversation(leadId)}
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      copyTextToClipboard(
                        leadDetailsToCopy,
                        "Lead details copied in multi-line format."
                      )
                    }
                  >
                    <Copy className="w-4 h-4 mr-1" />
                    Copy Lead Details
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={!lead.buyer_email}
                    onClick={() => copyTextToClipboard(lead.buyer_email, "Buyer email copied.")}
                  >
                    <Copy className="w-4 h-4 mr-1" />
                    Copy Email
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={!lead.buyer_phone}
                    onClick={() => copyTextToClipboard(lead.buyer_phone, "Buyer phone number copied.")}
                  >
                    <Copy className="w-4 h-4 mr-1" />
                    Copy Phone
                  </Button>
                </div>
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* --- Main Grid --- */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 space-y-4">
            {/* Q&A and Additional Details */}
            <Card>
              <CardHeader>
                <CardTitle>Project Details</CardTitle>
              </CardHeader>
              <CardContent>
                <h3 className="font-semibold mb-2 text-primary">Lead Questions & Answers</h3>
                <div className="space-y-3 text-sm">
                  {lead.qna?.length > 0 ? (
                    lead.qna.map((item, index) => (
                      <div key={index} className="grid grid-cols-3 gap-2">
                         <strong className="col-span-1 text-gray-600">{item.question_label}</strong>
                         <p className="col-span-2 text-gray-800">{JSON.stringify(item.answer_display)}</p>
                      </div>
                    ))
                  ) : (
                    <p className="text-muted-foreground">No Q&A provided.</p>
                  )}
                </div>
                <Separator className="my-4" />
                <h3 className="font-semibold mb-2 text-primary">Additional Details</h3>
                {hasAdditionalDetails ? (
                  <div className="space-y-4 text-sm">
                    {additionalDetailEntries.map(([key, value]) => (
                      <div key={key} className="grid gap-1 sm:grid-cols-3">
                        <strong className="text-gray-600">
                          {formatDetailLabel(key)}
                        </strong>
                        <div className="sm:col-span-2 text-gray-800">
                          <AdditionalDetailValue value={value} />
                        </div>
                      </div>
                    ))}

                    {additionalFiles.length > 0 && (
                      <div className="grid gap-2 sm:grid-cols-3">
                        <strong className="text-gray-600">Files</strong>
                        <div className="sm:col-span-2 flex flex-wrap gap-2">
                          {additionalFiles.map((file, index) => (
                            <Button
                              key={`${file.key}-${index}`}
                              asChild
                              size="sm"
                              variant="outline"
                            >
                              <a
                                href={getS3AssetUrl(file.key)}
                                target="_blank"
                                rel="noopener noreferrer"
                                title={file.key}
                              >
                                <ExternalLink className="w-4 h-4" />
                                {file.name}
                              </a>
                            </Button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    No additional details.
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Seller Interactions Table */}
            <LeadInteractionsTable data={lead.seller_interactions_table} />

            <Card>
              <CardContent className="pt-6">
                <AdminNotesSection
                  contextType="LEAD"
                  contextId={leadId}
                  contextTab="GENERAL"
                />
              </CardContent>
            </Card>

            {/* Communications History — lazy-loaded on expand */}
            <LeadCommsSection leadId={leadId} />
          </div>
          <div className="lg:col-span-1 space-y-4">
            <LeadDetailCard title="Buyer Details" details={buyerDetails} />
            <LeadDetailCard title="Location" details={locationDetails} />
            <LeadDetailCard title="Pricing" details={pricingDetails} />
            {lead.attribution && (
              <LeadDetailCard title="Lead Attribution" details={attributionDetails} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Skeleton component for loading state
const LeadDetailSkeleton = () => (
  <div className="p-4 space-y-4">
    <Skeleton className="h-24 w-full" />
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div className="lg:col-span-2 space-y-4">
        <Skeleton className="h-64 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
      <div className="lg:col-span-1 space-y-4">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    </div>
  </div>
);

export default LeadDetailPage;
